import { ValueOf } from "@typings/Cue";
import z from "zod";

// BT Backend API Payload Schemas
export const GetArticlePayloadSchema = z
  .object({
    id: z.string().optional(),
    urlPath: z.string().optional(),
    lang: z.string().optional(),
    platform: z.string().default("web"),
    t: z.number().optional(),
  })
  .refine(({ id, urlPath }) => id !== undefined || urlPath !== undefined, {
    message: "Either id or urlPath must be provided",
  });

export const GetArticleTagsPayloadSchema = z
  .object({
    urlPath: z.string().optional(),
    id: z.string().optional(),
    page: z.number().optional(), // page is optional for backward compatibility
    size: z.number(),
    platform: z.string(),
    offset: z.number().optional(),
    version: z.string().optional(),
    t: z.number().optional(),
  })
  .refine(({ id, urlPath }) => id !== undefined || urlPath !== undefined, {
    message: "Either id or urlPath must be provided",
  });

export const GetArticleSectionsPayloadSchema = z.object({
  page: z.number(),
  size: z.number(),
  platform: z.string(),
  sections: z.array(z.string()).nonempty("sections cannot be empty"),
  sortBy: z.string().optional(),
  t: z.number().optional(),
});

export type GetArticlePayload = z.infer<typeof GetArticlePayloadSchema>;
export type GetArticleTagsPayload = z.infer<typeof GetArticleTagsPayloadSchema>;
export type GetArticleSectionsPayload = z.infer<
  typeof GetArticleSectionsPayloadSchema
>;

export type BTBackendAPIEndpoints = {
  /** Retrieves detailed information about a specific article */
  "/v1/article": GetArticlePayload;
  /** Retrieves a paginated list of articles associated with the specified tag. Filtering can be done using either the tag ID or the absolute urlPath of the tag. If both are provided, the filter will apply based on the tag ID. */
  "/v1/articles/tags": GetArticleTagsPayload;
  /** Retrieves a paginated list of articles associated with the specified tag. Filtering can be done unique name of the section. */
  "/v1/articles/sections": GetArticleSectionsPayload;
};

export type BTBackendAPIEndpointsKeys = keyof BTBackendAPIEndpoints;
export type BTBackendAPIEndpointsValues = ValueOf<BTBackendAPIEndpoints>;

export const BT_BACKEND_API_ENDPOINTS_SCHEMA: Record<
  BTBackendAPIEndpointsKeys,
  {
    schema: z.ZodSchema;
    requestType: "GET" | "POST";
  }
> = {
  "/v1/article": {
    schema: GetArticlePayloadSchema,
    requestType: "GET",
  },
  "/v1/articles/tags": {
    schema: GetArticleTagsPayloadSchema,
    requestType: "GET",
  },
  "/v1/articles/sections": {
    schema: GetArticleSectionsPayloadSchema,
    requestType: "GET",
  },
};
